<template>
  <div class="video-center">
    <!-- 左侧：监控设备统计 + 设备列表 -->
    <div class="video-left-panel-wrapper">
      <div class="video-left-panel">
      <div class="card section-device-stats">
        <div class="section-header">
          <div class="section-title">监控设备统计</div>
          <button class="btn btn-sm" @click="showDetails">详情</button>
        </div>
        <div class="device-stats-grid">
          <div v-for="stat in deviceStats" :key="stat.label" class="device-stat-card">
            <div class="stat-icon">📹</div>
            <div class="stat-info">
              <div class="stat-value">{{ stat.value }}台</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card section-device-list">
        <div class="section-header">
          <div class="section-title">监控设备列表</div>
          <button class="btn btn-sm" @click="showDetails">详情</button>
        </div>
        <div class="device-list-scroll">
          <!-- 一级：小区 -->
          <div v-for="community in deviceList" :key="getNodeKey(community)" class="community-group">
            <div class="community-header" @click="toggleNode(community)">
              <span class="community-name">{{ community.label }}</span>
              <span class="device-count">{{ getDeviceCount(community) }}</span>
            </div>
            <!-- 二级：区域 -->
            <div v-if="isNodeExpanded(community) && community.children" class="device-categories">
              <template v-for="area in community.children">
                <div v-if="area.type === 'area'" :key="getNodeKey(area)" class="category-group">
                  <div class="category-header" @click="toggleNode(area)">
                    <span class="category-name">{{ area.label }}</span>
                  </div>
                  <!-- 三级：模型或设备 -->
                  <div v-if="isNodeExpanded(area) && area.children" class="device-categories">
                    <template v-for="item in area.children">
                      <!-- 如果是设备（三级，没有模型）- 先显示 -->
                      <div v-if="item.type === 'machine'" :key="getNodeKey(item)" class="category-group">
                        <div class="device-items">
                          <div
                            class="device-item"
                            :class="{ active: selectedDevice && selectedDevice.value === item.value }"
                            @click="selectDevice(item)"
                          >
                            <span class="device-status online">●</span>
                            <span class="device-name">{{ item.label }}</span>
                          </div>
                        </div>
                      </div>
                      <!-- 如果是模型（三级）- 后显示 -->
                      <div v-else-if="item.type === 'model'" :key="getNodeKey(item)" class="category-group">
                        <div class="category-header" @click="toggleNode(item)">
                          <span class="category-name">{{ item.label }}</span>
                        </div>
                        <!-- 四级：模型下的设备 -->
                        <div v-if="isNodeExpanded(item) && item.children" class="device-items">
                          <template v-for="device in item.children">
                            <div
                              v-if="device.type === 'machine'"
                              :key="getNodeKey(device)"
                              class="device-item"
                              :class="{ active: selectedDevice && selectedDevice.value === device.value }"
                              @click="selectDevice(device)"
                            >
                              <span class="device-status online">●</span>
                              <span class="device-name">{{ device.label }}</span>
                            </div>
                          </template>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- 中央：视频网格 -->
    <div class="video-center-panel">
      <div class="video-grid" :class="`grid-${layout}`">
        <div
          v-for="(video, idx) in displayedVideos"
          :key="idx"
          class="video-item"
          :class="{ 'video-item-selected': selectedVideoIndex === idx }"
          @click="selectVideoPosition(idx, $event)"
        >
          <div class="video-wrapper">
            <div :id="`jessibuca-container-${idx}`" class="jessibuca-container" v-if="video.url"></div>
            <div v-else class="video-placeholder">
              <div class="placeholder-icon">📹</div>
              <div class="placeholder-text">{{ video.name || '点击选择设备' }}</div>
            </div>
            <div class="video-overlay" v-if="video.url">
              <div class="video-device-name">{{ getCommunityDeviceName(video) }}</div>
            </div>
            <div v-if="selectedVideoIndex === idx" class="video-selected-indicator">
              <span>已选中</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 布局切换按钮 -->
      <div class="layout-controls">
        <button
          v-for="l in layouts"
          :key="l"
          class="layout-btn"
          :class="{ active: layout === l }"
          @click="layout = l"
        >
          {{ l }}×{{ l }}
        </button>
      </div>
    </div>

    <!-- 视频播放层（点击视频放大查看） -->
    <div v-if="videoPlayerVisible" class="video-mask" @click="closeVideoPlayer">
      <div ref="videoContainerRef" class="video-modal" @click.stop>
        <div class="video-header">
          <div class="video-title">{{ videoPlayerTitle }}</div>
          <div class="video-actions">
            <button class="icon-btn" type="button" title="全屏" @click="toggleVideoFullscreen">⛶</button>
            <button class="icon-btn" type="button" title="关闭" @click="closeVideoPlayer">×</button>
          </div>
        </div>
        <div class="video-body">
          <div v-if="videoPlayerUrl" ref="videoPlayerContainer" class="jessibuca-container-modal"></div>
          <div v-else class="video-empty">暂无视频流</div>
        </div>
      </div>
    </div>

    <!-- 右侧：告警统计 + 告警列表 -->
    <div class="video-right-panel-wrapper">
      <div class="video-right-panel">
      <div class="card section-alarm-stats">
        <div class="section-header">
          <div class="section-title">告警事件统计</div>
          <button class="btn btn-sm" @click="showDetails">详情</button>
        </div>
        <div class="alarm-stats-row">
          <div class="alarm-stat-item">
            <span class="stat-label">在线摄像机</span>
            <span class="stat-value">{{ onlineCameraCount }}台</span>
          </div>
          <div class="alarm-stat-item">
            <span class="stat-label">告警摄像机</span>
            <span class="stat-value alarm">{{ alarmCameraCount }}台</span>
          </div>
        </div>
        <div class="alarm-chart">
          <div ref="alarmChartRef" class="chart-container"></div>
        </div>
      </div>

      <div class="card section-alarm-list">
        <div class="alarm-tabs">
          <button class="tab-btn" :class="{ active: alarmTab === 'events' }" @click="alarmTab = 'events'">事件</button>
          <button class="tab-btn" :class="{ active: alarmTab === 'tickets' }" @click="alarmTab = 'tickets'">工单</button>
        </div>
        <div class="alarm-list-scroll">
          <div v-if="alarmTab === 'events'" class="alarm-table">
            <div class="table-header">
              <div class="th">事件类型</div>
              <div class="th">抓拍设备</div>
              <div class="th">告警时间</div>
              <div class="th">状态</div>
            </div>
            <div v-for="(event, idx) in alarmEvents" :key="idx" class="table-row">
              <div class="td">{{ event.type }}</div>
              <div class="td">{{ event.device }}</div>
              <div class="td">{{ event.time }}</div>
              <div class="td">
                <span class="status-badge" :class="event.status">{{ event.statusText }}</span>
              </div>
            </div>
          </div>
          <div v-else class="alarm-table">
            <div class="table-header">
              <div class="th">工单编号</div>
              <div class="th">事件类型</div>
              <div class="th">处理人</div>
              <div class="th">状态</div>
            </div>
            <div v-for="(ticket, idx) in alarmTickets" :key="idx" class="table-row">
              <div class="td">{{ ticket.id }}</div>
              <div class="td">{{ ticket.type }}</div>
              <div class="td">{{ ticket.handler }}</div>
              <div class="td">
                <span class="status-badge" :class="ticket.status">{{ ticket.statusText }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { communityStore } from '../stores/community'
import * as echarts from 'echarts'
import { REFRESH_CONFIG } from '../utils/refreshConfig'
import request from '@/utils/request'
import { getCurrentCommunity } from '@/utils/vc'
import { getCommunityConfig } from '../utils/communityConfig'
import { MessageBox } from 'element-ui'

export default {
  name: 'VideoCenter',
  data() {
    return {
      communityStore,
      alarmChart: null,
      layout: 2, // 2x2 grid
      layouts: [1, 2, 3, 4],
      alarmTab: 'events',
      selectedDevice: null,
      selectedVideoIndex: 0, // 当前选中的视频位置索引
      videoPlayerVisible: false,
      videoPlayerUrl: '',
      videoPlayerTitle: '视频监控',
      modalJessibuca: null, // 弹窗中的播放器实例
      deviceStats: [], // 动态从接口获取，不再写死
      loadingDeviceStats: false, // 统计数据加载状态
      deviceList: [], // 存储后端返回的树形数据
      loadingDeviceList: false,
      refreshTimer: null, // 设备列表刷新定时器
      statsRefreshTimer: null, // 设备统计刷新定时器
      expandedNodes: new Set(), // 存储展开的节点key
      jessibucaInstances: {}, // 存储每个视频的 Jessibuca 实例 { index: jessibucaInstance }
      videoList: [
        { id: '', name: '', deviceName: '', location: '', url: '' },
        { id: '', name: '', deviceName: '', location: '', url: '' },
        { id: '', name: '', deviceName: '', location: '', url: '' },
        { id: '', name: '', deviceName: '', location: '', url: '' }
      ],
      onlineCameraCount: 18,
      alarmCameraCount: 3,
      alarmEvents: [
        { type: '高空抛物', device: '卡口1', time: '2022-06-17 14:30', status: 'unassigned', statusText: '未派发' },
        { type: '垃圾满溢', device: '卡口2', time: '2022-06-17 14:25', status: 'unassigned', statusText: '未派发' },
        { type: '陌生人告警', device: '卡口3', time: '2022-06-17 14:20', status: 'unassigned', statusText: '未派发' },
        { type: '安全告警', device: '卡口4', time: '2022-06-17 14:15', status: 'assigned', statusText: '已派发' },
        { type: '安全告警', device: '卡口5', time: '2022-06-17 14:10', status: 'cancelled', statusText: '已取消' }
      ],
      alarmTickets: [
        { id: 'T001', type: '高空抛物', handler: '张三', status: 'processing', statusText: '处理中' },
        { id: 'T002', type: '垃圾满溢', handler: '李四', status: 'completed', statusText: '已完成' },
        { id: 'T003', type: '陌生人告警', handler: '王五', status: 'processing', statusText: '处理中' }
      ]
    }
  },
  computed: {
    displayedVideos() {
      const count = this.layout * this.layout
      return this.videoList.slice(0, count)
    },
    currentCommunityName() {
      // 获取当前小区名称
      try {
        // 方式1：从 getCurrentCommunity() 获取
        const currentCommunity = getCurrentCommunity()
        if (currentCommunity && currentCommunity.communityName) {
          console.log('从 getCurrentCommunity 获取小区名称:', currentCommunity.communityName)
          return currentCommunity.communityName
        }

        // 方式2：从 communityStore 获取（存储的是名称字符串）
        if (this.communityStore && this.communityStore.currentCommunity) {
          const communityKey = typeof this.communityStore.currentCommunity === 'string'
            ? this.communityStore.currentCommunity
            : (this.communityStore.currentCommunity.name || '')

          if (communityKey) {
            // 方式3：从 communityConfig 获取配置，确保获取到正确的名称
            const config = getCommunityConfig(communityKey)
            if (config && config.name) {
              console.log('从 communityConfig 获取小区名称:', config.name)
              return config.name
            }
            // 如果配置中没有，直接使用 communityKey
            console.log('从 communityStore 获取小区名称:', communityKey)
            return communityKey
          }
        }
      } catch (e) {
        console.warn('获取小区名称失败:', e)
      }
      console.warn('无法获取小区名称，返回空字符串')
      return ''
    }
  },
  watch: {
    'communityStore.currentCommunity'() {
      this.loadCommunityData()
    },
    layout() {
      // 布局切换时，重新初始化播放器（因为容器数量会变化）
      this.$nextTick(() => {
        this.reinitJessibucaPlayers()
      })
    }
  },
  mounted() {
    this.initAlarmChart()
    this.loadMonitorDeviceList()
    this.loadMonitorDeviceStats()

    // 定时刷新监控设备列表
    this.refreshTimer = setInterval(() => {
      this.loadMonitorDeviceList()
    }, REFRESH_CONFIG.VIDEO)

    // 定时刷新监控设备统计
    this.statsRefreshTimer = setInterval(() => {
      this.loadMonitorDeviceStats()
    }, REFRESH_CONFIG.VIDEO)

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // 清理定时器
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }

    if (this.statsRefreshTimer) {
      clearInterval(this.statsRefreshTimer)
      this.statsRefreshTimer = null
    }

    if (this.alarmChart) {
      this.alarmChart.dispose()
    }

    // 销毁所有 Jessibuca 播放器实例
    Object.keys(this.jessibucaInstances).forEach(index => {
      try {
        if (this.jessibucaInstances[index]) {
          this.jessibucaInstances[index].destroy()
        }
      } catch (err) {
        console.warn(`销毁播放器 ${index} 失败:`, err)
      }
    })
    this.jessibucaInstances = {}

    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    /**
     * 获取节点唯一key
     */
    getNodeKey(node) {
      return `${node.type}_${node.value}`
    },
    /**
     * 判断节点是否展开
     */
    isNodeExpanded(node) {
      return this.expandedNodes.has(this.getNodeKey(node))
    },
    /**
     * 切换节点展开/收起
     */
    toggleNode(node) {
      const key = this.getNodeKey(node)
      if (this.expandedNodes.has(key)) {
        this.expandedNodes.delete(key)
      } else {
        this.expandedNodes.add(key)
      }
      // 触发响应式更新
      this.$forceUpdate()
    },
    /**
     * 获取设备数量统计
     */
    getDeviceCount(community) {
      if (!community || !community.children) return '0/0'
      let total = 0
      let online = 0

      // 递归统计所有设备
      const countDevices = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return
        for (const node of nodes) {
          if (node.type === 'machine') {
            total++
            online++ // 暂时都算在线
          } else if (node.children) {
            countDevices(node.children)
          }
        }
      }

      countDevices(community.children)
      return `${online}/${total}`
    },
    selectDevice(device) {
      console.log('=== selectDevice 被调用 ===', {
        device: device,
        selectedVideoIndex: this.selectedVideoIndex,
        deviceId: device?.value,
        deviceLabel: device?.label
      })

      this.selectedDevice = device
      // 获取播放地址并更新视频列表，播放到当前选中的视频位置
      this.loadPlayVideoUrl(device, this.selectedVideoIndex)
    },
    /**
     * 选择视频位置（点击视频网格项时）
     */
    selectVideoPosition(videoIndex) {
      // 如果点击的是已有视频的内容区域，则全屏查看
      if (this.videoList[videoIndex] && this.videoList[videoIndex].url) {
        this.focusVideo(this.videoList[videoIndex])
        return
      }

      // 否则，选择该位置用于播放新视频
      this.selectedVideoIndex = videoIndex
    },
    /**
     * 加载设备播放地址
     * @param {Object} device 设备对象
     * @param {Number} videoIndex 视频位置索引，默认为当前选中的位置
     */
    loadPlayVideoUrl(device, videoIndex = null) {
      console.log('=== loadPlayVideoUrl 开始 ===', {
        device: device,
        videoIndex: videoIndex,
        deviceType: device?.type
      })

      if (!device || device.type !== 'machine') {
        console.warn('设备类型不正确或设备为空，退出')
        return
      }

      // 如果没有指定位置，使用当前选中的位置
      if (videoIndex === null) {
        videoIndex = this.selectedVideoIndex
      }

      // 检查该设备是否已经在其他位置播放
      const deviceId = String(device.value || '').trim()

      if (!deviceId) {
        console.warn('设备ID为空，无法检查重复播放')
        alert('设备ID为空，无法播放')
        return
      }

      console.log('=== 开始检查设备重复播放 ===', {
        deviceId: deviceId,
        deviceLabel: device.label,
        targetIndex: videoIndex,
        videoListLength: this.videoList.length,
        videoList: this.videoList.map((v, i) => ({
          index: i,
          id: String(v?.id || '').trim(),
          url: v?.url ? '有URL' : '无URL',
          hasPlayer: !!this.jessibucaInstances[i]
        }))
      })

      // 检查 videoList 中是否有相同设备ID且在不同位置的视频
      let existingIndex = -1
      for (let i = 0; i < this.videoList.length; i++) {
        if (i === videoIndex) {
          console.log(`跳过位置 ${i}（目标位置）`)
          continue // 跳过当前要播放的位置
        }

        const video = this.videoList[i]
        if (!video) {
          console.log(`位置 ${i} 没有视频数据`)
          continue
        }

        const videoId = String(video.id || '').trim()
        const hasUrl = video.url && String(video.url).trim() !== ''
        const hasPlayer = !!this.jessibucaInstances[i]

        console.log(`检查位置 ${i}:`, {
          videoId: videoId,
          deviceId: deviceId,
          isMatch: videoId === deviceId,
          hasUrl: hasUrl,
          hasPlayer: hasPlayer,
          shouldBlock: videoId === deviceId && (hasUrl || hasPlayer)
        })

        // 如果设备ID匹配，且有URL或播放器实例，说明该设备正在播放
        if (videoId === deviceId && (hasUrl || hasPlayer)) {
          existingIndex = i
          console.error('❌ 找到重复播放的设备！', {
            existingIndex: i,
            videoId: videoId,
            deviceId: deviceId,
            hasUrl: hasUrl,
            hasPlayer: hasPlayer,
            video: video
          })
          break
        }
      }

      if (existingIndex >= 0) {
        // 设备已在其他位置播放，提示用户
        const positionName = this.getPositionName(existingIndex)
        const message = `设备"${device.label}"已在${positionName}播放，请先停止该位置的播放后再切换`

        console.error('❌ 设备重复播放，阻止播放！', {
          message: message,
          existingIndex: existingIndex,
          targetIndex: videoIndex
        })

        // 使用 ElementUI 的 MessageBox 显示提示（统一样式，不显示浏览器标题）
        try {
          // 直接使用导入的 MessageBox.alert
          MessageBox.alert(message, '提示', {
            type: 'warning',
            confirmButtonText: '确定',
            closeOnClickModal: true,
            closeOnPressEscape: true
          }).catch(() => {
            // 用户关闭弹窗时的处理（可选）
          })
        } catch (error) {
          console.error('MessageBox.alert 调用失败:', error)
          // 备用方案1：尝试使用实例方法
          try {
            if (this.$alert) {
              this.$alert(message, '提示', {
                type: 'warning',
                confirmButtonText: '确定'
              }).catch(() => {})
            } else if (this.$msgbox) {
              this.$msgbox({
                message: message,
                title: '提示',
                type: 'warning',
                confirmButtonText: '确定',
                showCancelButton: false
              }).catch(() => {})
            } else {
              // 备用方案2：使用 Message 组件（非阻塞式提示）
              this.$message({
                message: message,
                type: 'warning',
                duration: 5000,
                showClose: true
              })
            }
          } catch (err2) {
            console.error('所有提示方法都失败:', err2)
            // 最后的备用方案：使用 Message 组件
            if (this.$message) {
              this.$message({
                message: message,
                type: 'warning',
                duration: 5000,
                showClose: true
              })
            }
          }
        }

        return
      }

      console.log('✅ 设备重复播放检查通过，继续播放')

      // 获取当前社区的 communityId
      let communityId = null
      try {
        const currentCommunity = getCurrentCommunity()
        if (currentCommunity && currentCommunity.communityId) {
          communityId = currentCommunity.communityId
        }
      } catch (e) {
        console.warn('获取 communityId 失败:', e)
        return
      }

      if (!communityId) {
        console.warn('communityId 为空，无法获取播放地址')
        return
      }

      // 调用接口获取播放地址
      request({
        url: '/app/bigScreen/getMonitorPlayVideoUrl',
        method: 'get',
        params: {
          machineId: device.value,
          communityId: communityId
        },
        showLoading: false
      }).then(response => {
        const res = response.data
        if (res && res.code === 0 && res.data) {
          // res.data 是字符串类型的播放地址URL
          const videoUrl = typeof res.data === 'string' ? res.data : String(res.data)

          console.log(`获取播放地址成功，位置 ${videoIndex}:`, videoUrl)

          // 确保 videoList 有足够的长度
          const maxIndex = this.layout * this.layout - 1
          while (this.videoList.length <= videoIndex && this.videoList.length <= maxIndex) {
            this.videoList.push({
              id: '',
              name: '',
              deviceName: '',
              location: '',
              url: ''
            })
          }

          // 如果该位置已有播放器，先销毁
          if (this.jessibucaInstances[videoIndex]) {
            try {
              this.jessibucaInstances[videoIndex].destroy()
              delete this.jessibucaInstances[videoIndex]
            } catch (err) {
              console.warn(`销毁位置 ${videoIndex} 的播放器失败:`, err)
            }
          }

          // 更新指定位置的视频项（只更新该位置的属性，避免触发整个列表的响应式更新）
          const existingVideo = this.videoList[videoIndex]
          if (existingVideo) {
            // 如果已存在，只更新属性
            existingVideo.id = device.value
            existingVideo.name = device.label
            existingVideo.deviceName = device.label
            existingVideo.location = device.label
            existingVideo.url = videoUrl
          } else {
            // 如果不存在，使用 $set 添加
            this.$set(this.videoList, videoIndex, {
              id: device.value,
              name: device.label,
              deviceName: device.label,
              location: device.label,
              url: videoUrl
            })
          }

          // 使用 Jessibuca 播放视频（延迟初始化，确保 DOM 已更新）
          this.$nextTick(() => {
            this.initJessibucaPlayer(videoIndex, videoUrl)
          })
        } else {
          console.warn('获取播放地址失败:', res)
        }
      }).catch(error => {
        console.error('获取播放地址异常:', error)
      })
    },
    /**
     * 获取小区名称：设备名称的格式
     */
    getCommunityDeviceName(video) {
      if (!video || !video.deviceName) {
        return ''
      }
      const communityName = this.currentCommunityName || ''
      if (communityName) {
        return `${communityName}：${video.deviceName}`
      }
      return video.deviceName
    },
    /**
     * 获取位置名称（用于提示信息）
     */
    getPositionName(index) {
      const row = Math.floor(index / this.layout) + 1
      const col = (index % this.layout) + 1
      return `第${row}行第${col}列`
    },
    /**
     * 查找第一个小区第一个有模型的子设备
     */
    findFirstDeviceWithModel() {
      if (!this.deviceList || this.deviceList.length === 0) {
        return null
      }

      // 遍历第一个小区的所有区域
      const firstCommunity = this.deviceList[0]
      if (!firstCommunity || !firstCommunity.children) {
        return null
      }

      // 遍历区域
      for (const area of firstCommunity.children) {
        if (area.type === 'area' && area.children) {
          // 遍历区域下的子项（设备或模型）
          for (const item of area.children) {
            // 如果是模型，查找模型下的第一个设备
            if (item.type === 'model' && item.children && item.children.length > 0) {
              // 找到模型下的第一个设备
              for (const device of item.children) {
                if (device.type === 'machine') {
                  return device
                }
              }
            }
          }
        }
      }

      return null
    },
    focusVideo(video) {
      // 点击视频放大查看
      this.videoPlayerTitle = video.deviceName || video.name || '视频监控'
      this.videoPlayerUrl = video.url || ''
      this.videoPlayerVisible = true

      // 在弹窗中初始化播放器
      this.$nextTick(() => {
        if (this.videoPlayerUrl && this.$refs.videoPlayerContainer) {
          this.initVideoPlayerModal(this.videoPlayerUrl)
        }
      })
    },
    /**
     * 在弹窗中初始化视频播放器
     */
    initVideoPlayerModal(videoUrl) {
      // 检查 Jessibuca 是否可用
      if (typeof window.Jessibuca === 'undefined') {
        console.error('Jessibuca 未加载')
        return
      }

      // 销毁旧的播放器（如果存在）
      if (this.modalJessibuca) {
        try {
          this.modalJessibuca.destroy()
        } catch (err) {
          console.warn('销毁弹窗播放器失败:', err)
        }
        this.modalJessibuca = null
      }

      const container = this.$refs.videoPlayerContainer
      if (!container) {
        return
      }

      try {
        // 创建播放器实例
        this.modalJessibuca = new window.Jessibuca({
          container: container,
          videoBuffer: 0.2,
          isResize: false,
          text: '',
          loadingText: '',
          useMSE: false,
          debug: false,
          isNotMute: false,
          supportDblclickFullscreen: false, // 禁用双击全屏
          operateBtns: {
            fullscreen: false, // 禁用全屏按钮
            screenshot: false, // 禁用截图按钮
            play: false, // 禁用播放/暂停按钮
            audio: false, // 禁用音频按钮
            recorder: false // 禁用录制按钮
          }
        })

        // 播放视频
        this.modalJessibuca.play(videoUrl)

        // 监听事件
        this.modalJessibuca.on('start', () => {
          console.log('弹窗视频开始播放')
        })

        this.modalJessibuca.on('error', (error) => {
          console.error('弹窗视频播放错误:', error)
        })

      } catch (error) {
        console.error('初始化弹窗播放器失败:', error)
      }
    },
    toggleVideoPlay(video) {
      // 切换视频播放/暂停（使用 Jessibuca）
      const videoIndex = this.videoList.findIndex(v => v.id === video.id)
      if (videoIndex >= 0) {
        const jessibuca = this.jessibucaInstances[videoIndex]
        if (jessibuca) {
          if (jessibuca.playing) {
            jessibuca.pause()
          } else {
            jessibuca.play()
          }
        }
      }
    },
    /**
     * 重新初始化所有播放器
     */
    reinitJessibucaPlayers() {
      // 销毁所有现有播放器
      Object.keys(this.jessibucaInstances).forEach(index => {
        try {
          if (this.jessibucaInstances[index]) {
            this.jessibucaInstances[index].destroy()
          }
        } catch (err) {
          console.warn(`销毁播放器 ${index} 失败:`, err)
        }
      })
      this.jessibucaInstances = {}

      // 重新初始化有 URL 的视频
      this.displayedVideos.forEach((video, index) => {
        if (video && video.url) {
          this.initJessibucaPlayer(index, video.url)
        }
      })
    },
    /**
     * 初始化 Jessibuca 播放器
     */
    initJessibucaPlayer(videoIndex, videoUrl) {
      // 检查 Jessibuca 是否可用
      if (typeof window.Jessibuca === 'undefined') {
        console.error('Jessibuca 未加载，请检查 /js/jessibuca/jessibuca.js 是否正确引入')
        return
      }

      // 销毁旧的播放器实例（如果存在）
      if (this.jessibucaInstances[videoIndex]) {
        try {
          this.jessibucaInstances[videoIndex].destroy()
        } catch (err) {
          console.warn('销毁旧播放器失败:', err)
        }
        delete this.jessibucaInstances[videoIndex]
      }

      // 获取容器元素
      const containerId = `jessibuca-container-${videoIndex}`
      const container = document.getElementById(containerId)

      if (!container) {
        console.warn(`容器元素 ${containerId} 不存在`)
        return
      }

      try {
        // 创建 Jessibuca 实例
        const jessibuca = new window.Jessibuca({
          container: container,
          videoBuffer: 0.2, // 缓存时长（秒）
          isResize: false, // 是否等比缩放
          text: '', // 水印文字
          loadingText: '', // 加载文字
          useMSE: false, // 不使用 MSE
          debug: false, // 调试模式
          isNotMute: false, // 静音
          supportDblclickFullscreen: false, // 禁用双击全屏
          operateBtns: {
            fullscreen: false, // 禁用全屏按钮
            screenshot: false, // 禁用截图按钮
            play: false, // 禁用播放/暂停按钮
            audio: false, // 禁用音频按钮
            recorder: false // 禁用录制按钮
          }
        })

        // 保存实例引用
        this.$set(this.jessibucaInstances, videoIndex, jessibuca)

        // 播放视频
        jessibuca.play(videoUrl)

        // 监听播放开始事件
        jessibuca.on('start', () => {
          console.log(`视频 ${videoIndex} 开始播放`)
        })

        // 监听错误事件
        jessibuca.on('error', (error) => {
          console.error(`视频 ${videoIndex} 播放错误:`, error)
        })

        // 监听超时事件
        jessibuca.on('timeout', (error) => {
          console.warn(`视频 ${videoIndex} 播放超时:`, error)
        })

      } catch (error) {
        console.error('初始化 Jessibuca 播放器失败:', error)
      }
    },
    closeVideoPlayer() {
      // 销毁弹窗中的播放器
      if (this.modalJessibuca) {
        try {
          this.modalJessibuca.destroy()
        } catch (err) {
          console.warn('销毁弹窗播放器失败:', err)
        }
        this.modalJessibuca = null
      }

      this.videoPlayerVisible = false
      this.videoPlayerUrl = ''
    },
    toggleVideoFullscreen() {
      const el = this.$refs.videoContainerRef
      if (!el) return
      if (!document.fullscreenElement) {
        el.requestFullscreen().catch(() => {})
      } else {
        document.exitFullscreen().catch(() => {})
      }
    },
    getCurrentTime() {
      const now = new Date()
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const weekday = weekdays[now.getDay()]
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      return `${year}年${month}月${day}日 ${weekday} ${hours}:${minutes}:${seconds}`
    },
    initAlarmChart() {
      if (!this.$refs.alarmChartRef) return
      this.alarmChart = echarts.init(this.$refs.alarmChartRef)
      this.alarmChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['告警事件', '告警工单'],
          textStyle: { color: '#a0b3c8', fontSize: 12 },
          bottom: 0
        },
        grid: { left: 40, right: 20, top: 30, bottom: 50 },
        xAxis: {
          type: 'category',
          data: ['06/12', '06/13', '06/14', '06/15', '06/16', '06/17'],
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
          axisLabel: { color: '#a0b3c8', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          name: '单位:条',
          nameTextStyle: { color: '#a0b3c8', fontSize: 11 },
          axisLine: { lineStyle: { color: 'rgba(255,255,255,0.2)' } },
          axisLabel: { color: '#a0b3c8', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)' } }
        },
        series: [
          {
            name: '告警事件',
            type: 'line',
            data: [320, 332, 301, 334, 390, 330],
            lineStyle: { color: '#00d4ff', width: 2 },
            itemStyle: { color: '#00d4ff' },
            areaStyle: { color: 'transparent' }
          },
          {
            name: '告警工单',
            type: 'line',
            data: [220, 182, 191, 234, 290, 330],
            lineStyle: { color: '#00ffff', width: 2 },
            itemStyle: { color: '#00ffff' },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(0,255,255,0.3)' },
                  { offset: 1, color: 'rgba(0,255,255,0)' }
                ]
              }
            }
          }
        ]
      })
    },
    loadCommunityData() {
      // 可以根据小区加载不同的设备列表和视频
      this.loadMonitorDeviceList()
      this.loadMonitorDeviceStats()
      this.initAlarmChart()
    },
    /**
     * 加载监控设备统计数据
     */
    loadMonitorDeviceStats() {
      if (this.loadingDeviceStats) return

      // 获取当前社区的 communityId
      let communityId = null
      try {
        const currentCommunity = getCurrentCommunity()
        if (currentCommunity && currentCommunity.communityId) {
          communityId = currentCommunity.communityId
        }
      } catch (e) {
        console.warn('获取 communityId 失败:', e)
        return
      }

      if (!communityId) {
        console.warn('communityId 为空，无法获取监控设备统计')
        return
      }

      this.loadingDeviceStats = true

      // 调用接口获取监控设备统计
      request({
        url: '/app/bigScreen/getMonitorDeviceStats',
        method: 'get',
        params: {
          communityId: communityId
        },
        showLoading: false
      }).then(response => {
        const res = response.data
        if (res && res.code === 0 && res.data && Array.isArray(res.data)) {
          // 直接使用后端返回的所有统计数据，动态显示
          this.deviceStats = res.data.map(stat => ({
            label: stat.label || '',
            value: stat.value || 0
          }))
        } else {
          console.warn('获取监控设备统计失败:', res)
          // 如果接口失败，设置为空数组
          this.deviceStats = []
        }
      }).catch(error => {
        console.error('获取监控设备统计异常:', error)
        // 异常时设置为空数组
        this.deviceStats = []
      }).finally(() => {
        this.loadingDeviceStats = false
      })
    },
    /**
     * 加载监控设备列表
     */
    loadMonitorDeviceList() {
      if (this.loadingDeviceList) return

      // 获取当前社区的 communityId
      let communityId = null
      try {
        const currentCommunity = getCurrentCommunity()
        if (currentCommunity && currentCommunity.communityId) {
          communityId = currentCommunity.communityId
        }
      } catch (e) {
        console.warn('获取 communityId 失败:', e)
        return
      }

      if (!communityId) {
        console.warn('communityId 为空，无法获取监控设备列表')
        return
      }

      this.loadingDeviceList = true

      // 调用接口获取监控设备列表
      request({
        url: '/app/bigScreen/getMonitorDeviceList',
        method: 'get',
        params: {
          communityId: communityId
        },
        showLoading: false
      }).then(response => {
        const res = response.data
        if (res && res.code === 0 && res.data) {
          // 直接使用后端返回的树形数据，但需要调整区域下子项的顺序
          // 先显示设备（无模型），后显示模型
          const sortedData = this.sortAreaChildren(res.data)
          this.deviceList = sortedData || []
          // 默认展开第一个小区及其所有子级
          if (this.deviceList.length > 0) {
            this.expandAllChildren(this.deviceList[0])

          // 默认选择第一个小区第一个有模型的子设备，播放到第一个位置
          this.$nextTick(() => {
            const firstDevice = this.findFirstDeviceWithModel()
            if (firstDevice) {
              this.selectedVideoIndex = 0 // 确保选中第一个位置
              this.selectDevice(firstDevice)
            }
          })
          }
        } else {
          console.warn('获取监控设备列表失败:', res)
          this.deviceList = []
        }
      }).catch(error => {
        console.error('获取监控设备列表异常:', error)
        this.deviceList = []
      }).finally(() => {
        this.loadingDeviceList = false
      })
    },
    /**
     * 对区域下的子项进行排序：先显示设备（无模型），后显示模型
     */
    sortAreaChildren(data) {
      if (!data || !Array.isArray(data)) return data

      // 深拷贝数据，避免修改原数据
      const sortedData = JSON.parse(JSON.stringify(data))

      // 遍历小区
      for (const community of sortedData) {
        if (community.children && Array.isArray(community.children)) {
          // 遍历区域
          for (const area of community.children) {
            if (area.type === 'area' && area.children && Array.isArray(area.children)) {
              // 对区域下的子项进行排序：设备在前，模型在后
              area.children.sort((a, b) => {
                // 设备（machine）排在前面
                if (a.type === 'machine' && b.type === 'model') return -1
                // 模型（model）排在后面
                if (a.type === 'model' && b.type === 'machine') return 1
                // 同类型保持原顺序
                return 0
              })
            }
          }
        }
      }

      return sortedData
    },
    /**
     * 递归展开节点的所有子级
     */
    expandAllChildren(node) {
      if (!node) return

      // 将当前节点添加到展开集合
      this.expandedNodes.add(this.getNodeKey(node))

      // 如果有子节点，递归展开所有子节点
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        for (const child of node.children) {
          this.expandAllChildren(child)
        }
      }
    },
    handleResize() {
      if (this.alarmChart) {
        this.alarmChart.resize()
      }
    },
    showDetails() {
      // TODO: 弹窗显示详情
    }
  }
}
</script>

<style lang="scss" scoped>
.video-center {
  display: grid;
  grid-template-columns: 400px 1fr 430px; // 与社区态势保持一致
  gap: 20px;
  height: 100%;
  overflow: hidden;
}

// ========== 左侧面板 ==========
.video-left-panel-wrapper {
  height: 100%;
  // border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  // padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden; // 外层容器隐藏溢出，内层处理滚动
  background: rgba(15, 30, 50, 0.6);
}

.video-left-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1; // 占据剩余空间
  min-height: 0; // 重要：允许 flex 子元素缩小
  overflow-y: auto; // 内容超出时显示滚动条
  overflow-x: hidden;
  max-height: 100%; // 限制最大高度，确保可以滚动

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }

  ::v-deep .card {
    padding: 10px; // 与社区态势保持一致
  }

  ::v-deep .section-title {
    margin-bottom: 0; // 与社区态势保持一致
    font-size: 14px; // 与社区态势保持一致
  }

  // 所有卡片默认根据内容自适应
  .card {
    flex: 0 0 auto;
    min-height: 0; // 允许收缩
  }

  // 最后一个卡片占据剩余空间，并设置为 flex 容器
  .card:last-child {
    flex: 1 0 auto; // 占据剩余空间，但不收缩，允许根据内容增长
    display: flex;
    flex-direction: column;
    // 不设置 min-height，让内容自然增长
    // 不设置 overflow，让内容自然溢出，由父容器 .video-left-panel 处理滚动
  }
}

.section-device-stats {
  // flex 由 .card:last-child 控制（当它是最后一个时）
}

.device-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.device-stat-card {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 255, 255, 0.05) 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  .stat-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    border-radius: 6px;
    background: rgba(0, 212, 255, 0.08);
    border: 1px solid rgba(0, 212, 255, 0.2);
  }
  .stat-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .stat-value {
    font-size: 18px;
    font-weight: bold;
    color: #00d4ff;
    line-height: 1.1;
  }
  .stat-label {
    font-size: 11px;
    color: #a0b3c8;
  }
}

.section-device-list {
  // flex 由 .card:last-child 控制（当它是最后一个时）
}

.device-list-scroll {
  flex: 1;
  min-height: 0;
  padding-right: 2px;
  overflow-y: auto; // 内容超出时显示滚动条

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
}

.community-group {
  margin-bottom: 8px;
  .community-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: rgba(0, 212, 255, 0.08);
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    .community-name {
      color: #fff;
      font-weight: 600;
      font-size: 13px;
    }
    .device-count {
      color: #00d4ff;
      font-size: 12px;
      font-weight: bold;
    }
    &:hover {
      background: rgba(0, 212, 255, 0.12);
    }
  }
}

.device-categories {
  margin-top: 6px;
  margin-left: 12px; // 设备类别相对于小区名称缩进
}

.category-group {
  margin-bottom: 6px;
  .category-header {
    padding: 6px 10px;
    color: #a0b3c8;
    font-size: 12px;
    cursor: pointer;
    user-select: none;
    border-radius: 4px;
    text-align: left; // 确保文本靠左显示
    .category-name {
      // 类别名称文本，从 padding-left: 10px 开始
    }
    &:hover {
      background: rgba(0, 212, 255, 0.05);
      color: #fff;
    }
  }
}

.device-items {
  margin-left: 0; // 区域下直接显示的设备（无模型）与 category-header 左对齐
  margin-top: 4px;
  padding-left: 10px; // 与 category-header 的 padding-left 一致，使圆点与类别文本左边缘对齐
}

// 模型下的设备应该有缩进（紧跟在 category-header 后面的 device-items）
.category-header + .device-items {
  margin-left: 12px; // 模型下的设备需要缩进
}

.device-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px 6px 0; // 左侧 padding 为 0，右侧保留 10px，因为父容器已有 padding-left
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  color: #a0b3c8;
  .device-status {
    font-size: 8px;
    &.online {
      color: #4caf50;
    }
    &.offline {
      color: #f44336;
    }
  }
  &:hover {
    background: rgba(0, 212, 255, 0.08);
  }
  &.active {
    background: rgba(0, 212, 255, 0.15);
    color: #fff;
  }
}

// ========== 中央视频区 ==========
.video-center-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  height: 100%;
}

.video-grid {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  gap: 12px;
  &.grid-1 {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }
  &.grid-2 {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }
  &.grid-3 {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
  &.grid-4 {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(4, 1fr);
  }
}

.video-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    border-color: rgba(0, 212, 255, 0.6);
  }
  &.video-item-selected {
    border: 2px solid #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  }
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-el {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

.jessibuca-container {
  width: 100%;
  height: 100%;
  background: #000;
  position: relative;
}

.jessibuca-container-modal {
  width: 100%;
  height: 100%;
  background: #000;
  position: relative;
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a0b3c8;
  .placeholder-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }
  .placeholder-text {
    font-size: 14px;
  }
}

.video-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 30%, transparent 70%, rgba(0, 0, 0, 0.6) 100%);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  pointer-events: none;
  .video-device-name {
    font-size: 12px;
    color: #fff;
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
    align-self: flex-end;
    margin-top: auto;
  }
  .video-selected-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 212, 255, 0.9);
    color: #000;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    z-index: 10;
    pointer-events: none;
  }
}

.layout-controls {
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  background: rgba(15, 30, 50, 0.6);
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.3);
}

.layout-btn {
  padding: 6px 14px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: transparent;
  color: #a0b3c8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  &:hover {
    border-color: rgba(0, 212, 255, 0.6);
    color: #00d4ff;
  }
  &.active {
    background: #00d4ff;
    border-color: #00d4ff;
    color: #000;
    font-weight: bold;
  }
}

// ========== 右侧面板 ==========
.video-right-panel-wrapper {
  height: 100%;
  // border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  // padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden; // 外层容器隐藏溢出，内层处理滚动
  background: rgba(15, 30, 50, 0.6);
}

.video-right-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1; // 占据剩余空间
  min-height: 0; // 重要：允许 flex 子元素缩小
  overflow-y: auto !important; // 内容超出时显示滚动条
  overflow-x: hidden;
  max-height: 100%; // 限制最大高度，确保可以滚动

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none !important; // Firefox
  -ms-overflow-style: none !important; // IE/Edge

  &::-webkit-scrollbar {
    display: none !important; // Chrome/Safari
    width: 0 !important;
    height: 0 !important;
  }

  &::-webkit-scrollbar-track {
    display: none !important;
  }

  &::-webkit-scrollbar-thumb {
    display: none !important;
  }

  ::v-deep .card {
    padding: 10px; // 与社区态势保持一致
  }

  ::v-deep .section-title {
    margin-bottom: 0; // 与社区态势保持一致
    font-size: 14px; // 与社区态势保持一致
  }

  // 所有卡片默认根据内容自适应
  .card {
    flex: 0 0 auto;
    min-height: 0; // 允许收缩
  }

  // 最后一个卡片占据剩余空间，并设置为 flex 容器
  .card:last-child {
    flex: 1 0 auto; // 占据剩余空间，但不收缩，允许根据内容增长
    display: flex;
    flex-direction: column;
    // 不设置 min-height，让内容自然增长
    // 不设置 overflow，让内容自然溢出，由父容器 .video-right-panel 处理滚动
  }
}

.section-alarm-stats {
  // flex 由 .card:last-child 控制（当它是最后一个时）
}

.alarm-stats-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.alarm-stat-item {
  flex: 1;
  padding: 10px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  .stat-label {
    font-size: 12px;
    color: #a0b3c8;
  }
  .stat-value {
    font-size: 20px;
    font-weight: bold;
    color: #00d4ff;
    &.alarm {
      color: #ff6b6b;
    }
  }
}

.alarm-chart {
  height: 200px;
  .chart-container {
    width: 100%;
    height: 100%;
  }
}

.section-alarm-list {
  // flex 由 .card:last-child 控制（当它是最后一个时）
}

.alarm-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
}

.tab-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: #a0b3c8;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.2s;
  &:hover {
    color: #00d4ff;
  }
  &.active {
    color: #00d4ff;
    border-bottom-color: #00d4ff;
    font-weight: bold;
  }
}

.alarm-list-scroll {
  flex: 1;
  min-height: 0;
  padding-right: 2px;
  overflow-y: auto; // 内容超出时显示滚动条

  // 隐藏滚动条但保留滚动功能
  scrollbar-width: none; // Firefox
  -ms-overflow-style: none; // IE/Edge

  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
}

.alarm-table {
  .table-header {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 0.8fr;
    gap: 8px;
    padding: 8px 10px;
    background: rgba(0, 212, 255, 0.1);
    border-radius: 6px;
    margin-bottom: 8px;
    .th {
      font-size: 12px;
      font-weight: bold;
      color: #00d4ff;
    }
  }
  .table-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 0.8fr;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 4px;
    margin-bottom: 6px;
    font-size: 12px;
    color: #a0b3c8;
    &:hover {
      background: rgba(0, 212, 255, 0.05);
    }
  }
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  &.unassigned {
    background: rgba(255, 107, 107, 0.2);
    color: #ff6b6b;
  }
  &.assigned {
    background: rgba(0, 212, 255, 0.2);
    color: #00d4ff;
  }
  &.cancelled {
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }
  &.processing {
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
  }
  &.completed {
    background: rgba(158, 158, 158, 0.2);
    color: #9e9e9e;
  }
}

// ========== 视频播放层 ==========
.video-mask {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
}
.video-modal {
  width: min(980px, calc(100% - 40px));
  height: min(620px, calc(100% - 40px));
  background: rgba(15, 30, 50, 0.92);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.video-header {
  height: 44px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
}
.video-title {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
.video-actions {
  display: flex;
  gap: 6px;
}
.icon-btn {
  width: 32px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(0, 212, 255, 0.06);
  color: rgba(255, 255, 255, 0.92);
  cursor: pointer;
  line-height: 26px;
  font-size: 16px;
  &:hover {
    background: rgba(0, 212, 255, 0.14);
  }
}
.video-body {
  flex: 1;
  position: relative;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.video-el {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}
.video-empty {
  color: #a0b3c8;
  font-size: 14px;
}

// 通用样式
.card {
  background: rgba(15, 30, 50, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 10px;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-title {
  font-size: 14px;
  margin-bottom: 0;
}
</style>
